import * as React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "src/common/utils";

import { stripHtmlComments } from "./markdownPreprocessor";
import { TIME_MARKER, TimeMarker } from "./TimeMarker";

export interface MarkdownPreviewProps {
  children: string;
  className?: string;
  maxLength?: number;
}

/**
 * A simplified version of Markdown that renders basic formatting
 * but strips out interactive elements like source references and step references.
 */
export const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({
  children,
  className,
  maxLength
}) => {
  // Clean the text to remove interactive elements
  const cleanText = React.useMemo(() => {
    const text = String(children || "");
    const cleanedText = cleanMarkdownText(text);

    if (maxLength && cleanedText.length > maxLength) {
      return cleanedText.substring(0, maxLength) + "...";
    }

    return cleanedText;
  }, [children, maxLength]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-1">{children}</p>,
        // Handle code blocks similar to the main Markdown component
        code: ({ children }) => {
          const text = String(children);

          // Time marker code blocks → render as interactive TimeMarker
          if (text.startsWith(TIME_MARKER) && text.endsWith(TIME_MARKER)) {
            const timestamp = text.slice(1, -1);
            return <TimeMarker>{timestamp}</TimeMarker>;
          }

          // Step reference code blocks (we skip these)
          const stepReferenceMatch = text.match(/\u2020\u2020step_([A-Za-z0-9_]+)\u2020\u2020/);
          if (stepReferenceMatch) {
            return null;
          }

          // Source reference code blocks (we skip these)
          const sourceReferenceMatch = text.match(/\u2020\u2020source_([A-Za-z0-9_]+)_([A-Za-z0-9_]+)\u2020\u2020/);
          if (sourceReferenceMatch) {
            return null;
          }

          // Check for call reference patterns
          const callReferenceMatch = text.match(/`\u2020\u2020call_[^\]]*`/);
          if (callReferenceMatch) {
            return null;
          }

          // Check for tool reference patterns
          const tooluReferenceMatch = text.match(/`\u2020\u2020toolu_[^\]]*`/);
          if (tooluReferenceMatch) {
            return null;
          }

          // Check for tool reference patterns
          const toolReferenceMatch = text.match(/`\u2020\u2020tool_[^\]]*`/);
          if (toolReferenceMatch) {
            return null;
          }

          // Check for artifact reference patterns
          const artifactReferenceMatch = text.match(/`\u2020\u2020artifact_[\w-]+\.json\u2020\u2020`/);
          if (artifactReferenceMatch) {
            return null;
          }

          // Catch-all: any remaining ††...†† marker rendered as code block
          if (text.includes("\u2020\u2020")) {
            return null;
          }

          // Regular code blocks (we render with same styling as Markdown)
          return (
            <code className="bg-accent text-amber-700 dark:text-amber-500 border border-border rounded-md px-1.5 py-0.5 text-xs">
              {text}
            </code>
          );
        },
        // Render other headings as simplified spans
        h1: ({ children }) => <span className="font-semibold text-sm">{children}</span>,
        h2: ({ children }) => <span className="font-semibold text-sm">{children}</span>,
        h3: ({ children }) => <span className="font-medium text-sm">{children}</span>,
        h4: ({ children }) => <span className="font-medium text-sm">{children}</span>,
        h5: ({ children }) => <span className="font-medium text-sm">{children}</span>,
        h6: ({ children }) => <span className="font-medium text-sm">{children}</span>,
      }}
      className={cn(
        "prose-sm max-w-none",
        "dark:prose-invert",
        "prose-p:leading-normal prose-li:leading-normal",
        "[&_ul]:list-disc [&_ul]:pl-5",
        "[&_ol]:list-decimal [&_ol]:pl-5",
        className,
      )}
    >
      {cleanText}
    </ReactMarkdown>
  );
};

/**
 * Helper function to clean markdown text by removing interactive elements
 */
export function cleanMarkdownText(text: string): string {
  // Remove HTML comments like <!-- hide this -->
  let cleaned = stripHtmlComments(text);

  // Remove call reference patterns like `††call_...`
  cleaned = cleaned.replace(/`\u2020\u2020call_[^\]]*`/g, "");

  // Remove tool reference patterns like `††toolu_...`
  cleaned = cleaned.replace(/`\u2020\u2020toolu_[^\]]*`/g, "");

  // Remove tool reference patterns like `††tool_...`
  cleaned = cleaned.replace(/`\u2020\u2020tool_[^\]]*`/g, "");

  // Remove artifact reference patterns like `††...json††` (V2 format without artifact_ prefix)
  cleaned = cleaned.replace(/`\u2020\u2020[\w-]+\.json\u2020\u2020`/g, "");

  // Remove step references like ††step_X††
  const stepReferenceRegex = /\u2020\u2020step_([A-Za-z0-9_]+)\u2020\u2020/g;
  cleaned = cleaned.replace(stepReferenceRegex, "");

  // Remove source references like ††source_nodeId_sourceId††
  const sourceReferenceRegex = /\u2020\u2020source_([A-Za-z0-9_]+)_([A-Za-z0-9_]+)\u2020\u2020/g;
  cleaned = cleaned.replace(sourceReferenceRegex, "");

  // Catch-all: strip any remaining bare ††...†† markers not caught above
  cleaned = cleaned.replace(/\u2020\u2020[^\u2020]+\u2020\u2020/g, "");

  // Wrap §timestamp§ in backticks so they render as TimeMarker via the code component
  cleaned = cleaned.replace(/(?<!`)§([0-9TZ:.+-]+)§(?!`)/g, "`§$1§`");

  // Collapse runs of horizontal whitespace (preserve newlines for markdown structure)
  cleaned = cleaned.replace(/[^\S\n]{2,}/g, " ");

  return cleaned;
}

export default MarkdownPreview;
